import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import { Button } from '../Button/Button';
import { Label } from '../Label/Label';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  subLabel?: string;
  helperText?: string;
  hasError?: boolean;
  icon?: React.ReactNode;
  tooltip?: string | React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      subLabel,
      helperText,
      hasError,
      icon,
      tooltip,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setPasswordVisible] = useState(false);

    const togglePasswordVisibility = () => {
      setPasswordVisible(!isPasswordVisible);
    };

    const shouldForceLowercase =
      type === 'email' ||
      (typeof props.name === 'string' &&
        props.name.toLowerCase().includes('email'));

    const {
      onChange: onChangeProp,
      onBlur: onBlurProp,
      value: valueProp,
      ...restProps
    } = props;

    const normalizedValue =
      shouldForceLowercase && typeof valueProp === 'string'
        ? valueProp.toLowerCase()
        : valueProp;

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      if (shouldForceLowercase) {
        const next = e.currentTarget.value.toLowerCase();
        if (next !== e.currentTarget.value) {
          e.currentTarget.value = next;
        }
      }
      onChangeProp?.(e);
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
      if (shouldForceLowercase) {
        const next = e.currentTarget.value.toLowerCase();
        if (next !== e.currentTarget.value) {
          e.currentTarget.value = next;
        }
      }
      onBlurProp?.(e);
    };

    return (
      <div className='grid w-full items-center gap-2'>
        {label && (
          <div className='flex items-center gap-1.5'>
            <Label className='!text-white' htmlFor={label}>
              {label}
            </Label>
            {tooltip && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type='button' className='text-zinc-400 hover:text-zinc-300 transition-colors'>
                      <HelpCircle className='w-4 h-4' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side='right'
                    className='max-w-xs bg-zinc-900 border-zinc-700 text-zinc-200 p-3'
                  >
                    {typeof tooltip === 'string' ? (
                      <p className='text-sm whitespace-pre-line'>{tooltip}</p>
                    ) : (
                      tooltip
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
        <div className='relative'>
          {icon && (
            <div className='absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[#A1A1AA]'>
              {icon}
            </div>
          )}
          <input
            id={label}
            type={isPasswordVisible ? 'text' : type}
            className={cn(
              `flex h-10 w-full disabled:bg-bgDisabled rounded-md border bg-[#09090B] px-4 py-2 text-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50
              ${icon ? 'pl-10' : 'pl-3'}
              ${hasError || helperText ? 'border-borderError' : 'border-colorBorder'}`,
              className
            )}
            ref={ref}
            autoCapitalize='none'
            value={normalizedValue}
            onChange={handleChange}
            onBlur={handleBlur}
            {...restProps}
          />

          {type === 'password' ? (
            <Button
              type='button'
              onClick={togglePasswordVisibility}
              size='icon'
              className='absolute right-2 top-1/2 -translate-y-1/2 flex text-white items-center justify-center'
              aria-label={isPasswordVisible ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {isPasswordVisible ? <EyeOff /> : <Eye />}
            </Button>
          ) : null}
        </div>
        {subLabel && <p className='text-sm text-zinc-500'>{subLabel}</p>}
        {helperText && <p className='text-sm text-error'>{helperText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
